<template>
  <BaseLayout title="Competition">
    <div class="container my-4">
      <h1 class="display-4 text-center">Competition</h1>
      <div v-if="loading" class="text-center my-4">
        <div class="spinner-border" role="status">
          <span class="sr-only">&nbsp;&nbsp;Loading...</span>
        </div>
      </div>
      <div v-else>
        <div v-if="csvRows.length === 0" class="alert alert-info text-center">
          No competition results available.
          <button @click="fetchCompetitionCsv" class="btn btn-primary">
            <i class="bi bi-arrow-clockwise me-2"></i> Refresh
          </button>
        </div>
        <div v-else>
          <div v-if="lastTimeUpdated" class="mb-2 text-end text-muted">
            <p>
              <i class="bi bi-info-circle ms-2" v-tooltip="'Results are regenerated every day after midnight or manually by teacher.'"></i>
              Last time updated at: {{ formattedLastTimeUpdated }}
            </p>
          </div>
          <div class="table-responsive">
            <table class="table table-bordered table-striped">
              <thead>
                <tr>
                  <th v-for="header in csvHeaders" :key="header">{{ header }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, idx) in csvRows" :key="idx">
                  <td v-for="header in csvHeaders" :key="header">{{ row[header] }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
    <template v-slot:right-sidebar>
      <div v-if="isTeacher" class="card p-2 mb-2">
        <h5 class="card-title">Quick Actions:</h5>
        <router-link v-if="isTeacher" to="/competition/edit" class="btn btn-primary mb-2">
          <i class="bi bi-gear"></i> Competition settings
        </router-link>
        <button @click="generateCsvResults" class="btn btn-secondary mb-2" :disabled="loading">
          <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
          <i v-else class="bi bi-arrow-repeat"></i>
          <span class="ms-1">Recalculate results</span>
        </button>
        <button @click="downloadCsv" class="btn btn-success mb-2" :disabled="!csvBlob || downloadingCSV">
          <span v-if="downloadingCSV" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
          <i v-else class="bi bi-download"></i>
          <span class="ms-1">Download CSV</span>
        </button>
      </div>
    </template>
    <StemErrorModal ref="stemErrorModal"/>
  </BaseLayout>
</template>

<script>
import BaseLayout from '@/components/BaseLayout.vue';
import CompetitionController from '@/controllers/CompetitionController';
import StemErrorModal from '@/components/StemErrorModal.vue';
import Subtask from '@/models/subtask';
import { mapGetters } from 'vuex';

export default {
  name: 'CompetitionView',
  components: {
    BaseLayout,
    StemErrorModal
  },
  data() {
    return {
      csvHeaders: [],
      csvRows: [],
      loading: true,
      csvBlob: null,
      downloadingCSV: false,
      lastTimeUpdated: null,
    };
  },
  computed: {
    ...mapGetters(['isTeacher']),
    formattedLastTimeUpdated() {
     return this.lastTimeUpdated ? Subtask.formatDateTimeZone(this.lastTimeUpdated) : '';
    }
  },
  async created() {
    await this.fetchCompetitionCsv();
  },
  methods: {
    async generateCsvResults() {
      this.loading = true;
      try {
        const response = await CompetitionController.generateCompetitionResultsCsv();
        if (response.status === 200) {
          this.$refs.stemErrorModal.show('Success', 'CSV results generated successfully.');
        }
        await this.fetchCompetitionCsv(); // Refresh the CSV data
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      } finally {
        this.loading = false;
      }
    },
    async fetchCompetitionCsv() {
      this.loading = true;
      this.csvHeaders = [];
      this.csvRows = [];
      try {
        const response = await CompetitionController.downloadCompetitionResultsCsv();
        this.csvBlob = response.data;
        const text = await response.data.text();
        this.parseCsv(text);

        // Get the modified time from the custom header
        const modifiedTime = response.headers['x-competition-csv-modified-time'];
        this.lastTimeUpdated = modifiedTime;
      } catch (error) {
        // Do not show error modal
        console.warn('Failed to fetch competition CSV:', error);
      } finally {
        this.loading = false;
      }
    },
    parseCsv(csvText) {
      const lines = csvText.trim().split('\n');
      if (lines.length === 0) return;
      this.csvHeaders = lines[0].split(',').map(h => h.trim());
      this.csvRows = lines.slice(1).map(line => {
        const values = line.split(',').map(v => v.trim());
        const row = {};
        this.csvHeaders.forEach((header, idx) => {
          row[header] = values[idx];
        });
        return row;
      });
    },
    downloadCsv() {
      if (!this.csvBlob) return;
      try {
        this.downloadingCSV = true;
        const url = window.URL.createObjectURL(this.csvBlob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'competition_results.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
      catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      }
      finally {
        this.downloadingCSV = false;
      }
    }
  },
};
</script>